import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Loader2, Sparkles, ImageIcon, Play, Film, Users } from "lucide-react";

export default function PublicProfile() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [profile, setProfile] = useState(null);
  const [generations, setGenerations] = useState([]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/public/profile/${slug}`);
        setProfile(data.profile);
        setGenerations(data.generations || []);
      } catch {
        setNotFound(true);
      } finally { setLoading(false); }
    })();
  }, [slug]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] glyph-bg text-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10">
        {loading ? (
          <div className="py-32 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-[#F2C94C] animate-spin" />
          </div>
        ) : notFound ? (
          <div className="py-32 text-center">
            <div className="font-mono-hy text-[10px] text-white mb-2">// 404</div>
            <p className="font-display text-3xl text-white mb-4">This dynasty is private or doesn't exist.</p>
            <Link
              to="/gallery"
              data-testid="profile-404-back-link"
              className="font-mono-hy text-[11px] text-[#F2C94C] hover:underline"
            >
              ← Browse the Public Dynasty
            </Link>
          </div>
        ) : (
          <>
            {/* Profile header */}
            <section
              data-testid="public-profile-header"
              className="relative overflow-hidden rounded-3xl border border-[#F2C94C]/25 bg-gradient-to-br from-[#120E15] via-[#120E15] to-[#0d1f24] mb-10"
            >
              <div className="h-1 bg-gradient-to-r from-[#F2C94C] via-[#FF6B4A] to-[#00E5FF]" />
              <div className="absolute -top-24 -right-24 w-[380px] h-[380px] rounded-full bg-[#F2C94C]/8 blur-[100px] pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-[320px] h-[320px] rounded-full bg-[#00E5FF]/10 blur-[100px] pointer-events-none" />

              <div className="relative p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start md:items-center">
                <div className="w-24 h-24 rounded-2xl overflow-hidden bg-gradient-to-br from-[#F2C94C] to-[#FF6B4A] border border-[#F2C94C]/40 flex items-center justify-center shadow-[0_0_36px_-8px_rgba(242,201,76,0.55)] flex-shrink-0">
                  {profile.picture ? (
                    <img src={profile.picture} alt={profile.name} className="w-full h-full object-cover" />
                  ) : (
                    <Sparkles className="w-10 h-10 text-[#0A0A0A]" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-hy text-[10px] text-[#F2C94C] tracking-widest mb-2">// GENEO AI · CREATOR</div>
                  <h1 className="font-display text-4xl md:text-5xl text-white leading-tight" data-testid="public-profile-name">
                    {profile.name}
                  </h1>
                  <div className="mt-2 font-mono-hy text-[11px] text-[#00E5FF]" data-testid="public-profile-slug">
                    /u/{profile.slug}
                  </div>
                  <div className="mt-4 flex items-center gap-4 flex-wrap">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F2C94C]/10 border border-[#F2C94C]/30">
                      <ImageIcon className="w-3.5 h-3.5 text-[#F2C94C]" />
                      <span className="font-mono-hy text-[10px] text-[#F2C94C]" data-testid="public-profile-count">
                        {profile.generation_count} {profile.generation_count === 1 ? "work" : "works"}
                      </span>
                    </div>
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30">
                      <Users className="w-3.5 h-3.5 text-[#00E5FF]" />
                      <span className="font-mono-hy text-[10px] text-[#00E5FF]">PUBLIC · OPEN VAULT</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Generations grid */}
            {generations.length === 0 ? (
              <div className="py-24 text-center">
                <div className="font-mono-hy text-[10px] text-white mb-2">// EMPTY VAULT</div>
                <p className="font-display text-2xl text-white">
                  {profile.name} hasn't published any works yet.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="public-profile-grid">
                {generations.map((g) => (
                  <div
                    key={g.generation_id}
                    data-testid={`public-item-${g.generation_id}`}
                    className="group relative overflow-hidden rounded-2xl border border-[#F2C94C]/15 bg-[#120E15] aspect-square"
                  >
                    {g.video_url ? (
                      <>
                        <video src={g.video_url} muted loop playsInline autoPlay className="w-full h-full object-cover" />
                        <div className="absolute top-2 left-2 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#0A0A0A]/80 border border-[#FF6B4A]/40 text-[#FF6B4A] flex items-center gap-1">
                          <Film className="w-3 h-3" /> REEL
                        </div>
                      </>
                    ) : g.image_url ? (
                      <>
                        <img src={g.image_url} alt={g.prompt || ""} className="w-full h-full object-cover" />
                        {g.type === "turnaround" && (
                          <div className="absolute top-2 left-2 font-mono-hy text-[9px] px-2 py-0.5 rounded-full bg-[#0A0A0A]/80 border border-[#00E5FF]/40 text-[#00E5FF]">
                            ◇ 4-VIEWS
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full bg-[#120E15]" />
                    )}
                    <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-[#0A0A0A]/95 via-[#0A0A0A]/40 to-transparent">
                      <div className="font-body text-[11px] text-white line-clamp-2">
                        {g.prompt || "Untitled"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer CTA */}
            <div className="mt-16 text-center">
              <div className="font-mono-hy text-[10px] text-white tracking-widest mb-3">// CREATED WITH</div>
              <Link
                to="/studio"
                data-testid="public-profile-cta"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#F2C94C] text-[#0A0A0A] hover:bg-[#F2D06B] font-mono-hy text-xs transition-all hover:-translate-y-0.5 shadow-[0_0_28px_-6px_rgba(242,201,76,0.5)]"
              >
                <Sparkles className="w-4 h-4" /> Create your own dynasty
              </Link>
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
